import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMapPin, FiDollarSign, FiHome, FiClock, FiChevronRight, FiArrowLeft } from 'react-icons/fi';
import Navbar from '../../components/navbar/navbar';
import UserNav from '../../components/navbar/user-nav';
import Footer from '../../components/footer';
import SEO from '../../components/SEO';
import { useUser } from '../../context/UserContext';
import { getZonaBySlug, ZONAS_DATA, Zona } from '../../data/zonas-data';

export default function ZonaDetail() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const { user } = useUser();
  const navigate = useNavigate();

  const zona = slug ? getZonaBySlug(slug) : undefined;

  if (!zona) {
    return (
      <>
        {user ? (
          <UserNav
            nombreUsuario={user.nombre}
            nombreInmobiliaria={user.inmobiliaria_nombre}
          />
        ) : (
          <Navbar transparent={false} />
        )}
        <div className="container py-5 text-center">
          <h1>Zona no encontrada</h1>
          <p>La zona que buscas no existe.</p>
          <Link to="/zonas" className="btn btn-primary">Ver todas las zonas</Link>
        </div>
        <Footer />
      </>
    );
  }

  const zonaSchema = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: zona.nombreCompleto,
    description: zona.descripcion,
    url: `https://ampirivieranayarit.com/modulob/zonas/${zona.slug}`,
    geo: {
      '@type': 'GeoCoordinates',
      latitude: zona.coordenadas.lat,
      longitude: zona.coordenadas.lng
    },
    address: {
      '@type': 'PostalAddress',
      addressRegion: 'Nayarit',
      addressCountry: 'MX'
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const otrasZonas = ZONAS_DATA.filter(z => z.slug !== zona.slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`Propiedades en ${zona.nombre} - Bienes Raíces`}
        description={zona.metaDescription}
        path={`/modulob/zonas/${zona.slug}`}
        keywords={zona.keywords.join(', ')}
        schema={zonaSchema}
      />

      {user ? (
        <UserNav
          nombreUsuario={user.nombre}
          nombreInmobiliaria={user.inmobiliaria_nombre}
        />
      ) : (
        <Navbar transparent={false} />
      )}

      {/* Breadcrumb */}
      <section className="py-3 bg-light border-bottom">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/">Inicio</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/zonas">Zonas</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {zona.nombre}
              </li>
            </ol>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section
        className="py-5"
        style={{
          backgroundColor: '#0a2f27',
          backgroundImage: `linear-gradient(rgba(10, 47, 39, 0.85), rgba(10, 47, 39, 0.95))`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link text-white-50 p-0 mb-3"
              >
                <FiArrowLeft className="me-2" />
                Volver
              </button>
              <h1 className="text-white fw-bold display-5 mb-3">
                Propiedades en {zona.nombre}
              </h1>
              <p className="text-white-50 fs-5 mb-4">
                {zona.descripcion}
              </p>
              <div className="d-flex flex-wrap gap-3">
                <span className="badge bg-light text-dark px-3 py-2">
                  <FiMapPin className="me-2" />
                  {zona.ubicacion}
                </span>
                <span className="badge bg-light text-dark px-3 py-2">
                  <FiDollarSign className="me-2" />
                  {formatPrice(zona.rangoPreciosUSD.min)} - {formatPrice(zona.rangoPreciosUSD.max)}
                </span>
              </div>
            </div>
            <div className="col-lg-4 text-center text-lg-end mt-4 mt-lg-0">
              <Link
                to={`/grid?zona=${zona.slug}`}
                className="btn btn-lg px-4 py-3"
                style={{ backgroundColor: '#02de7f', color: '#0a2f27', fontWeight: 600 }}
              >
                <FiHome className="me-2" />
                Ver Propiedades en {zona.nombre}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Descripción Detallada */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <h2 className="fw-bold mb-4" style={{ color: '#0a2f27' }}>
                Sobre {zona.nombre}
              </h2>
              <p className="lead text-muted mb-4">
                {zona.descripcionLarga}
              </p>

              {/* Características */}
              <h3 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                Características Principales
              </h3>
              <div className="row g-3 mb-4">
                {zona.caracteristicas.map((caracteristica, idx) => (
                  <div className="col-md-6" key={idx}>
                    <div className="d-flex align-items-center">
                      <span
                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                        style={{
                          width: '32px',
                          height: '32px',
                          backgroundColor: 'rgba(2, 222, 127, 0.15)',
                          color: '#02de7f'
                        }}
                      >
                        <FiChevronRight />
                      </span>
                      <span>{caracteristica}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tipos de Propiedades */}
              <h3 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                Tipos de Propiedades Disponibles
              </h3>
              <div className="d-flex flex-wrap gap-2 mb-4">
                {zona.tiposPropiedades.map((tipo, idx) => (
                  <span key={idx} className="badge bg-light text-dark px-3 py-2">
                    {tipo}
                  </span>
                ))}
              </div>

              {/* Puntos Clave */}
              <h3 className="fw-bold mb-3" style={{ color: '#0a2f27' }}>
                ¿Por qué invertir en {zona.nombre}?
              </h3>
              <div className="row g-4 mb-4">
                {zona.puntosClave.map((punto, idx) => (
                  <div className="col-md-4" key={idx}>
                    <div
                      className="p-4 h-100 rounded-3"
                      style={{ backgroundColor: '#f8f9fa' }}
                    >
                      <h5 className="fw-bold mb-2" style={{ color: '#0a2f27' }}>
                        {punto.titulo}
                      </h5>
                      <p className="text-muted mb-0 small">
                        {punto.descripcion}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              {/* Card de Distancias */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">
                    <FiClock className="me-2" />
                    Distancias
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled mb-0">
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>Aeropuerto PVR</span>
                      <strong>{zona.distancias.aeropuerto}</strong>
                    </li>
                    <li className="d-flex justify-content-between py-2 border-bottom">
                      <span>Puerto Vallarta</span>
                      <strong>{zona.distancias.puertoVallarta}</strong>
                    </li>
                    <li className="d-flex justify-content-between py-2">
                      <span>Playa</span>
                      <strong>{zona.distancias.playa}</strong>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Card de Rango de Precios */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header" style={{ backgroundColor: '#0a2f27', color: 'white' }}>
                  <h5 className="mb-0">
                    <FiDollarSign className="me-2" />
                    Rango de Precios
                  </h5>
                </div>
                <div className="card-body text-center">
                  <p className="text-muted mb-2">Desde</p>
                  <h3 className="fw-bold" style={{ color: '#02de7f' }}>
                    {formatPrice(zona.rangoPreciosUSD.min)}
                  </h3>
                  <p className="text-muted mb-2">Hasta</p>
                  <h3 className="fw-bold" style={{ color: '#0a2f27' }}>
                    {formatPrice(zona.rangoPreciosUSD.max)}
                  </h3>
                </div>
              </div>

              {/* Amenidades */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-light">
                  <h5 className="mb-0">Amenidades</h5>
                </div>
                <div className="card-body">
                  <div className="d-flex flex-wrap gap-2">
                    {zona.amenidades.map((amenidad, idx) => (
                      <span
                        key={idx}
                        className="badge px-3 py-2"
                        style={{ backgroundColor: 'rgba(2, 222, 127, 0.15)', color: '#0a2f27' }}
                      >
                        {amenidad}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA Contacto */}
              <div
                className="card border-0 text-white"
                style={{ backgroundColor: '#0a2f27' }}
              >
                <div className="card-body text-center p-4">
                  <h5 className="fw-bold mb-3">¿Interesado en {zona.nombre}?</h5>
                  <p className="text-white-50 small mb-3">
                    Nuestros asesores certificados AMPI te ayudan a encontrar la propiedad ideal.
                  </p>
                  <Link
                    to="/contact"
                    className="btn btn-light w-100 mb-2"
                  >
                    Contactar Asesor
                  </Link>
                  <Link
                    to="/agents"
                    className="btn btn-outline-light w-100"
                  >
                    Ver Asesores en la Zona
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Otras Zonas */}
      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="fw-bold mb-4 text-center" style={{ color: '#0a2f27' }}>
            Explora Otras Zonas
          </h2>
          <div className="row g-4">
            {otrasZonas.map((otraZona) => (
              <div className="col-lg-4" key={otraZona.id}>
                <Link
                  to={`/zonas/${otraZona.slug}`}
                  className="card border-0 shadow-sm h-100 text-decoration-none"
                  style={{ borderRadius: '12px', overflow: 'hidden' }}
                >
                  <div
                    className="card-header py-3"
                    style={{ backgroundColor: '#0a2f27' }}
                  >
                    <h5 className="text-white mb-0">{otraZona.nombre}</h5>
                  </div>
                  <div className="card-body">
                    <p className="text-muted small mb-3">
                      {otraZona.descripcion}
                    </p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small">
                        Desde {formatPrice(otraZona.rangoPreciosUSD.min)}
                      </span>
                      <span className="text-primary">
                        Ver zona <FiChevronRight />
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/zonas" className="btn btn-outline-primary">
              Ver Todas las Zonas
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-5" style={{ backgroundColor: '#0a2f27' }}>
        <div className="container text-center">
          <h2 className="text-white fw-bold mb-3">
            Encuentra tu propiedad ideal en {zona.nombre}
          </h2>
          <p className="text-white-50 mb-4">
            Más de 300 propiedades disponibles con asesores certificados AMPI
          </p>
          <div className="d-flex justify-content-center gap-3 flex-wrap">
            <Link
              to={`/grid?zona=${zona.slug}`}
              className="btn btn-lg px-5"
              style={{ backgroundColor: '#02de7f', color: '#0a2f27', fontWeight: 600 }}
            >
              Explorar Propiedades
            </Link>
            <Link
              to="/contact"
              className="btn btn-lg btn-outline-light px-5"
            >
              Contactar Asesor
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
